import { compact, countBy, isNil } from 'es-toolkit';

import type { ClientPortalNavRoute } from './constants';
import type {
  ClientPortalCrumb,
  ClientPortalNavAvailability,
  ClientPortalNavId,
  ClientPortalNavState,
  ClientPortalPortfolioOption,
} from './types';
import {
  CLIENT_PORTAL_ACCOUNT_ROUTE,
  CLIENT_PORTAL_COMING_SOON_LABEL,
  CLIENT_PORTAL_HOME_ROUTE,
  CLIENT_PORTAL_INVESTMENTS_RESTRICTED_LABEL,
  CLIENT_PORTAL_NAV_LABELS,
  CLIENT_PORTAL_NAV_ROUTES,
  CLIENT_PORTAL_PERSONAL_PORTFOLIO_LABEL,
  CLIENT_PORTAL_RESTRICTED_LABEL,
  CLIENT_PORTAL_SHARED_MATERIALS_ROUTE,
  CLIENT_PORTAL_TASK_BADGE_CAP,
} from './constants';

export function isPresentString(value: string | null | undefined): value is string {
  return !isNil(value) && value.trim() !== '';
}

function stripQuery(path: string) {
  const queryStart = path.indexOf('?');
  return queryStart === -1 ? path : path.slice(0, queryStart);
}

const HOME_NAV_ROUTE: ClientPortalNavRoute = {
  navId: 'home',
  prefix: CLIENT_PORTAL_HOME_ROUTE,
  sectionHref: CLIENT_PORTAL_HOME_ROUTE,
};

function matchClientPortalNavRoute(path: string | null | undefined): ClientPortalNavRoute | null {
  if (!isPresentString(path)) {
    return null;
  }
  const pathname = stripQuery(path);
  if (pathname === CLIENT_PORTAL_HOME_ROUTE) {
    return HOME_NAV_ROUTE;
  }
  const matches = CLIENT_PORTAL_NAV_ROUTES.filter(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  if (matches.length === 0) {
    return null;
  }
  return matches.reduce((best, candidate) => (candidate.prefix.length > best.prefix.length ? candidate : best));
}

export function matchClientPortalNavId(path: string | null | undefined): ClientPortalNavId | null {
  return matchClientPortalNavRoute(path)?.navId ?? null;
}

export function buildClientPortalCrumbs({
  path,
  leaf,
  parent,
}: {
  path: string | null | undefined;
  leaf?: string | null;
  parent?: ClientPortalCrumb | null;
}): ClientPortalCrumb[] {
  const home: ClientPortalCrumb = { label: 'Home', href: CLIENT_PORTAL_HOME_ROUTE };
  const route = matchClientPortalNavRoute(path);

  if (isNil(route)) {
    return isPresentString(leaf) ? [home, { label: leaf }] : [{ label: 'Home' }];
  }

  if (route.navId === 'home') {
    return [{ label: 'Home' }];
  }

  const sectionLabel = CLIENT_PORTAL_NAV_LABELS[route.navId];
  const linkedSection: ClientPortalCrumb = { label: sectionLabel, href: route.sectionHref ?? undefined };

  if (isPresentString(leaf) && leaf !== sectionLabel) {
    return compact([home, linkedSection, parent, { label: leaf }]);
  }

  if (isNil(parent)) {
    return [home, { label: sectionLabel }];
  }

  return [home, linkedSection, { label: parent.label }];
}

export function clientPortalBackCrumb({
  path,
  personalPortfolioId,
  leaf,
  parent,
}: {
  readonly path: string | null | undefined;
  readonly personalPortfolioId: string | null;
  readonly leaf?: string | null;
  readonly parent?: ClientPortalCrumb | null;
}): ClientPortalCrumb | null {
  const navId = matchClientPortalNavId(path);
  if (navId === 'home') {
    return null;
  }
  const sectionHref = isNil(navId) ? null : clientPortalNavHref(navId, personalPortfolioId);
  const namesOwnLeaf = isPresentString(leaf) || !isNil(parent);
  const atSectionLanding = isNil(navId) || sectionHref === stripQuery(path ?? '');
  if (!namesOwnLeaf && atSectionLanding) {
    return null;
  }
  if (!isNil(parent) && isPresentString(parent.href)) {
    return parent;
  }
  if (!isNil(navId) && isPresentString(sectionHref)) {
    return { label: CLIENT_PORTAL_NAV_LABELS[navId], href: sectionHref };
  }
  return { label: CLIENT_PORTAL_NAV_LABELS.home, href: CLIENT_PORTAL_HOME_ROUTE };
}

export function clientPortalNavHref(navId: ClientPortalNavId, personalPortfolioId: string | null): string | null {
  switch (navId) {
    case 'home':
      return CLIENT_PORTAL_HOME_ROUTE;
    case 'yourTeam':
      return '/dashboard/advisor';
    case 'tasks':
      return '/tasks';
    case 'investments':
      return isPresentString(personalPortfolioId) ? `/savvy-portfolio/${personalPortfolioId}` : null;
    case 'accounts':
      return '/portfolio';
    case 'tax':
      return '/dashboard/tax';
    case 'planning':
      return '/financial-plan';
    case 'documents':
      return CLIENT_PORTAL_SHARED_MATERIALS_ROUTE;
    case 'reportIssue':
      return null;
    case 'account':
      return CLIENT_PORTAL_ACCOUNT_ROUTE;
  }
}

export function clientDisplayName(firstName: string | null | undefined, lastName: string | null | undefined): string {
  const fullName = [firstName, lastName].filter(isPresentString).join(' ');
  return isPresentString(fullName) ? fullName : 'Account';
}

export function clientPortalNavAvailability(
  navId: ClientPortalNavId,
  nav: Pick<
    ClientPortalNavState,
    'personalPortfolioId' | 'enableTaxTab' | 'investmentsDisabled' | 'hiddenPortalSurfaces' | 'supportEnabled'
  >,
): ClientPortalNavAvailability {
  if (nav.hiddenPortalSurfaces.includes(navId)) {
    return 'hidden';
  }
  if (navId === 'reportIssue') {
    return nav.supportEnabled ? 'available' : 'hidden';
  }
  if (navId === 'investments') {
    if (nav.investmentsDisabled) {
      return 'restricted';
    }
    return isPresentString(nav.personalPortfolioId) ? 'available' : 'comingSoon';
  }
  if (navId === 'tax') {
    return nav.enableTaxTab ? 'available' : 'restricted';
  }
  return 'available';
}

export function isClientPortalNavPathHidden(
  path: string | null | undefined,
  hiddenPortalSurfaces: readonly ClientPortalNavId[],
): boolean {
  const navId = matchClientPortalNavId(path);
  if (isNil(navId)) {
    return false;
  }
  return hiddenPortalSurfaces.includes(navId);
}

export function clientPortalNavUnavailableLabel(
  navId: ClientPortalNavId,
  availability: Extract<ClientPortalNavAvailability, 'restricted' | 'comingSoon'>,
): string {
  if (availability === 'comingSoon') {
    return CLIENT_PORTAL_COMING_SOON_LABEL;
  }
  if (navId === 'investments') {
    return CLIENT_PORTAL_INVESTMENTS_RESTRICTED_LABEL;
  }
  return CLIENT_PORTAL_RESTRICTED_LABEL;
}

export function formatTaskBadgeCount(count: number): string {
  return count > CLIENT_PORTAL_TASK_BADGE_CAP ? `${CLIENT_PORTAL_TASK_BADGE_CAP}+` : String(count);
}

function portfolioOptionLabel(portfolio: {
  readonly name: string;
  readonly displayName?: string | null;
  readonly isPersonal: boolean;
}): string {
  if (portfolio.isPersonal) {
    return CLIENT_PORTAL_PERSONAL_PORTFOLIO_LABEL;
  }
  return isPresentString(portfolio.displayName) ? portfolio.displayName : portfolio.name;
}

export function clientPortalPortfolioOptions(
  portfolios:
    | readonly {
        readonly id: string | number;
        readonly name: string;
        readonly displayName?: string | null;
        readonly isPersonal: boolean;
      }[]
    | null
    | undefined,
): readonly ClientPortalPortfolioOption[] {
  const options = [...(portfolios ?? [])]
    .sort((left, right) => Number(right.isPersonal) - Number(left.isPersonal))
    .map((portfolio) => ({
      id: String(portfolio.id),
      label: portfolioOptionLabel(portfolio),
      isPersonal: portfolio.isPersonal,
    }));
  const labelCounts = countBy(options, (option) => option.label);
  const takenLabels = new Set(options.map((option) => option.label));
  const lastOccurrence = new Map<string, number>();
  return options.map((option) => {
    if ((labelCounts[option.label] ?? 0) < 2) {
      return option;
    }
    let occurrence = (lastOccurrence.get(option.label) ?? 0) + 1;
    while (takenLabels.has(`${option.label} (${occurrence})`)) {
      occurrence += 1;
    }
    lastOccurrence.set(option.label, occurrence);
    const label = `${option.label} (${occurrence})`;
    takenLabels.add(label);
    return { ...option, label };
  });
}
